import {
  Children,
  Fragment,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button, Popconfirm, Popover, Space } from 'antd';
import { Ellipsis } from 'lucide-react';

type Owner = symbol;

type PageActionRegistration = {
  owner: Owner;
  pageKey: string;
  content: ReactNode;
};

type PageActionsContextValue = {
  pageKey: string;
  register: (owner: Owner, content: ReactNode) => () => void;
};

const PageActionsContext = createContext<PageActionsContextValue | null>(null);

function flattenActions(content: ReactNode): ReactNode[] {
  return Children.toArray(content).flatMap((item) => {
    if (!isValidElement(item)) return [];
    if (item.type === Fragment || item.type === Space) {
      return flattenActions((item.props as { children?: ReactNode }).children);
    }
    return [item];
  });
}

function isButtonAction(item: ReactNode): boolean {
  if (!isValidElement(item)) return false;
  if (item.type === Button || item.type === Popconfirm) return true;
  const nested = Children.toArray((item.props as { children?: ReactNode }).children);
  return nested.length === 1 && isButtonAction(nested[0]);
}

function compactActions(content: ReactNode) {
  const actions = flattenActions(content);
  if (actions.length <= 3 || !actions.every(isButtonAction)) return content;

  const leading = actions[0];
  const primary = actions.slice(-2);
  const overflow = actions.slice(1, -2);
  return (
    <div className="spark-page-actions">
      {leading}
      <Popover
        placement="bottomRight"
        trigger="click"
        content={<div className="spark-page-actions__overflow">{overflow}</div>}
      >
        <Button icon={<Ellipsis aria-hidden size={17} />}>更多操作</Button>
      </Popover>
      {primary}
    </div>
  );
}

export function PageActionsProvider({
  pageKey,
  children,
}: {
  pageKey: string;
  children: (actions: ReactNode) => ReactNode;
}) {
  const [registration, setRegistration] = useState<PageActionRegistration | null>(null);
  const register = useCallback((owner: Owner, content: ReactNode) => {
    setRegistration({ owner, pageKey, content });
    return () => setRegistration((current) => current?.owner === owner ? null : current);
  }, [pageKey]);
  const value = useMemo(() => ({ pageKey, register }), [pageKey, register]);
  const actions = registration?.pageKey === pageKey ? registration.content : null;

  return (
    <PageActionsContext.Provider value={value}>
      {children(actions)}
    </PageActionsContext.Provider>
  );
}

export function PageActions({ children }: { children?: ReactNode }) {
  const context = useContext(PageActionsContext);
  const owner = useRef<Owner>(Symbol('page-actions'));
  useLayoutEffect(() => {
    if (!context || !children) return;
    return context.register(owner.current, compactActions(children));
  }, [children, context]);
  return null;
}
